import {Map,InfoWindow,useMap,useMapsLibrary,Marker} from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useGetFidelNearestLocations } from "../../CustomHooks/Queries";

// Cluster Marker Component
function ClusterMarkers({ locations, onSelect }) {
  const map = useMap();
  const mapsLib = useMapsLibrary("maps");
  const markerLib = useMapsLibrary("marker");

  const clusterRef = useRef(null);
  const didFitBounds = useRef(false);

  useEffect(() => {
    if (!map || !mapsLib || !markerLib || !locations.length) return;

    clusterRef.current?.clearMarkers();

    const markers = locations.map((loc) => {
      const marker = new markerLib.Marker({
        position: {
          lat: loc.lat,
          lng: loc.lng,
        },
      });

      marker.addListener("click", () => {
        onSelect(loc);
      });

      return marker;
    });

    clusterRef.current = new MarkerClusterer({
      markers,
      map,
    });


    if (!didFitBounds.current) {
      const bounds = new window.google.maps.LatLngBounds();

      locations.forEach((loc) => {
        bounds.extend({
          lat: loc.lat,
          lng: loc.lng,
        });
      });

      if (locations.length === 1) {
        map.setCenter(locations[0]);
        map.setZoom(14);
      } else {
        map.fitBounds(bounds);
      }

      didFitBounds.current = true;
    }

    return () => clusterRef.current?.clearMarkers();
  }, [map, mapsLib, markerLib, locations]);

  return null;
}

function MapLoadMoreOnZoom({ loadMoreEnabled, hasNextPage, isFetchingNextPage, fetchNextPage }) {
  const map = useMap();
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!map || !loadMoreEnabled || !hasNextPage || isFetchingNextPage) return;

    const handler = () => {
      if (!hasNextPage || isFetchingNextPage) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        fetchNextPage();
      }, 500);
    };

    const listener = map.addListener?.("idle", handler);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      listener?.remove?.();
    };
  }, [map, loadMoreEnabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return null;
}

// MAIN MAP COMPONENT
export default function CustomMap({ onMapClick, residentId, expanded = false,latitude,longitude,highlightSingle = false, }) {
  const loadMoreRef = useRef(null);

  //  Big map: load more on scroll/zoom from start. Small map: never (click navigates away)
  const loadMoreEnabled = expanded;

  const {data,fetchNextPage,hasNextPage,isFetchingNextPage} = useGetFidelNearestLocations({ resident_id: residentId },{ enabled: !!residentId });

  const [selectedLocation, setSelectedLocation] = useState(null);

  //  Small map: click = navigate. Big map: click does nothing special
  const handleMapClick = useCallback((e) => {
      if (!expanded) {
        onMapClick?.(e);
      }
    },
    [onMapClick, expanded]
  );

  // FLATTEN PAGES
  const validLocations = useMemo(() => {
    return data?.pages?.flatMap((page) => page?.data?.data || []) || [];
  }, [data]);

  // GROUP LOCATIONS
  const groupedLocations = useMemo(() => {
    if (!validLocations.length) return [];

    const grouped = {};

    validLocations.forEach((loc) => {
      const lat = Math.round(Number(loc.latitude) * 100000) / 100000;
      const lng = Math.round(Number(loc.longitude) * 100000) / 100000;

      if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) return;

      const key = `${lat}-${lng}`;

      if (!grouped[key]) {
        grouped[key] = {lat,lng,offers: [],};
      }

      grouped[key].offers.push(loc);
    });

    return Object.values(grouped);
  }, [validLocations]);

  useEffect(() => {
  if (!hasNextPage || isFetchingNextPage) return;
  fetchNextPage();
}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // INFINITE SCROLL - only when expanded (big map) 
  useEffect(() => {
    if (!loadMoreEnabled || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        root: null,
        rootMargin: "600px",
        threshold: 0,
      }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreEnabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const offerCount = selectedLocation?.offers?.length ?? 0;


  return (
      <Map mapId = {process.env.REACT_APP_MAP_ID}
        defaultCenter={highlightSingle && latitude && longitude? { lat: latitude, lng: longitude }: { lat: 39.8283, lng: -98.5795 }}
        defaultZoom={highlightSingle ? 14 : 4}
        gestureHandling="greedy"
        style={{
        width: "100%",
        height: expanded ? "100vh" : "400px"
      }}
        onClick={handleMapClick}
        options={{
        mapTypeControl: false,     
        fullscreenControl: false,   
        streetViewControl: false, 
        zoomControl: true,          
  }}
        
      >
        {highlightSingle && latitude && longitude ? (
        <Marker
          position={{ lat: latitude, lng: longitude }}
          onClick={() =>
            setSelectedLocation({
              lat: latitude,
              lng: longitude,
              offers: validLocations.filter(
                (loc) =>
                  Number(loc.latitude) === latitude &&
                  Number(loc.longitude) === longitude
              ),
            })
          }
        />
          ) : (
            <ClusterMarkers
              locations={groupedLocations}
              onSelect={setSelectedLocation}
            />
          )}

        <MapLoadMoreOnZoom
          loadMoreEnabled={loadMoreEnabled}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
        />

        {selectedLocation && (
          <InfoWindow
            position={{
              lat: selectedLocation.lat,
              lng: selectedLocation.lng,
            }}
            onCloseClick={() => setSelectedLocation(null)}
          >
            <div style={{ minWidth: 240 }}>
              <h3>
                {offerCount} {offerCount === 1 ? "offer" : "offers"}
              </h3>

              {selectedLocation.offers.map((loc, i) => (
                <div key={i}>
                  <strong>{loc.offer?.brand_name}</strong>

                  <div>{loc.offer?.offer_name}</div>

                  <div style={{ color: "green" }}>
                    {loc.offer?.reward_value}% OFF
                  </div>

                  <hr />
                </div>
              ))}
            </div>
          </InfoWindow>
        )}
      </Map>
   
  );
}
